export type MessageHandler = (payload: unknown) => void;

export interface MessageEnvelope {
  data?: { type?: unknown; payload?: unknown } | null | undefined;
  origin?: string;
}

export interface MessageRouterOptions<T extends string = string> {
  handlers: Partial<Record<T, MessageHandler>>;
  expectedOrigin?: string | null;
}

/**
 * The first message with a non-empty origin locks the router's accepted origin
 * (unless expectedOrigin was provided upfront). This trust-on-first-use pattern
 * lets the iframe work before it knows the parent's origin, then hardens afterwards.
 * Framing-specific guards (e.g. event.source === window.parent) stay in the hook.
 *
 * Parameterize T with a string-literal union (e.g. `InboundMessageType`) so typos
 * in handler keys fail at compile time instead of silently no-op'ing at runtime.
 */
export class MessageRouter<T extends string = string> {
  private lockedOrigin: string | null;
  private readonly handlers: Partial<Record<T, MessageHandler>>;

  constructor(options: MessageRouterOptions<T>) {
    this.handlers = options.handlers;
    this.lockedOrigin = options.expectedOrigin ?? null;
  }

  route(event: MessageEnvelope): boolean {
    const origin = event.origin;
    if (this.lockedOrigin && origin && origin !== this.lockedOrigin) {
      return false;
    }
    if (!this.lockedOrigin && origin) {
      this.lockedOrigin = origin;
    }

    const data = event.data;
    if (!data || typeof data.type !== 'string') return false;
    const handler = this.handlers[data.type as T];
    if (!handler) return false;

    handler(data.payload);
    return true;
  }

  getState(): { lockedOrigin: string | null } {
    return { lockedOrigin: this.lockedOrigin };
  }
}
